import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Building2, Users, ArrowLeft, Plus, Pencil, Trash2, Phone, Mail, UserCog, MapPin, Loader2, BadgePlus } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const empty = { name: "", manager: "", phone: "", email: "", address: "", notes: "" };

const BranchForm = ({ open, onOpenChange, initial, onSaved }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isEdit = Boolean(initial?.id);
  useEffect(() => { if (open) setForm({ ...empty, ...(initial || {}) }); }, [open, initial]);

  const save = async () => {
    if (!form.name.trim()) { toast.warning(t("dash.branches.warnName")); return; }
    setBusy(true);
    try {
      if (isEdit) await api.updateCompanyBranch(initial.id, form);
      else await api.createCompanyBranch(form);
      toast.success(isEdit ? t("dash.branches.updated") : t("dash.branches.created"));
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.common.saveFail")); }
    finally { setBusy(false); }
  };

  const FIELDS = [
    { k: "name", label: t("dash.branches.fName"), icon: Building2, ph: t("dash.branches.fNamePh") },
    { k: "manager", label: t("dash.branches.fManager"), icon: UserCog, ph: t("dash.branches.fManagerPh") },
    { k: "phone", label: t("dash.branches.fPhone"), icon: Phone, ph: "05xxxxxxxx", ltr: true },
    { k: "email", label: t("dash.branches.fEmail"), icon: Mail, ph: "branch@company.com", ltr: true },
    { k: "address", label: t("dash.branches.fAddress"), icon: MapPin, ph: t("dash.branches.fAddressPh") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir={dir} data-testid="branch-form-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{isEdit ? t("dash.branches.editTitle") : t("dash.branches.createTitle")}</DialogTitle>
          <DialogDescription className="text-right">{t("dash.branches.dialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <div key={f.k} className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-slate-600"><f.icon className="h-3.5 w-3.5" /> {f.label}</Label>
              <Input data-testid={`branch-${f.k}-input`} dir={f.ltr ? "ltr" : "rtl"} placeholder={f.ph}
                value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-slate-600">{t("dash.branches.notes")}</Label>
            <Input data-testid="branch-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="branch-save-btn" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("dash.common.save")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.common.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BranchesPanel = ({ onSelectBranch, isManager = false }) => {
  const { t, dir } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    try { setRows(await api.companyBranches()); } catch { toast.error(t("dash.branches.loadFail")); }
    finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try { await api.deleteCompanyBranch(delTarget.id); toast.success(t("dash.branches.deleted")); setDelTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("dash.common.deleteFail")); }
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="branches-panel" dir={dir}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-900">{t("dash.branches.title", { n: rows.length })}</h2>
          <p className="text-sm text-slate-400">{t("dash.branches.sub")}</p>
        </div>
        {isManager && (
          <Button data-testid="branch-create-btn" onClick={() => { setEditing(null); setFormOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            <Plus className="h-4 w-4" /> {t("dash.branches.createBtn")}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div data-testid="branches-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-heading text-lg text-slate-600">{t("dash.branches.emptyTitle")}</p>
          <p className="text-sm text-slate-400">{t("dash.branches.emptySub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="branches-grid">
          {rows.map((b, i) => (
            <motion.div key={b.id || b.name} data-testid={`branch-card-${b.name}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all p-5">
              <div className="flex items-start justify-between">
                <button onClick={() => onSelectBranch?.(b.name)} className="flex items-center gap-2.5 text-right group">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <span className="font-heading font-bold text-slate-900 group-hover:text-blue-600 transition">{b.name}</span>
                    <div className="flex items-center gap-1 text-slate-500 text-xs mt-0.5">
                      <Users className="h-3.5 w-3.5" /><span className="font-mono-nums">{b.employee_count}</span> {t("dash.branches.empSuffix")}
                    </div>
                  </div>
                </button>
                {isManager && (
                  <div className="flex items-center gap-1">
                    {b.unregistered ? (
                      <button data-testid={`branch-register-${b.name}`} title={t("dash.branches.register")}
                        onClick={() => { setEditing({ name: b.name }); setFormOpen(true); }}
                        className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><BadgePlus className="h-4 w-4" /></button>
                    ) : (
                      <>
                        <button data-testid={`branch-edit-${b.name}`} onClick={() => { setEditing(b); setFormOpen(true); }}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`branch-delete-${b.name}`} onClick={() => setDelTarget(b)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {(b.manager || b.phone || b.email || b.address) && (
                <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-xs text-slate-500">
                  {b.manager && <div className="flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" /> {b.manager}</div>}
                  {b.phone && <div className="flex items-center gap-1.5" dir="ltr"><Phone className="h-3.5 w-3.5" /> {b.phone}</div>}
                  {b.email && <div className="flex items-center gap-1.5" dir="ltr"><Mail className="h-3.5 w-3.5" /> {b.email}</div>}
                  {b.address && <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {b.address}</div>}
                </div>
              )}
              {b.unregistered && <div className="mt-3 text-[11px] text-amber-600 bg-amber-50 rounded-lg px-2 py-1">{t("dash.branches.unregistered")}</div>}
            </motion.div>
          ))}
        </div>
      )}

      <BranchForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} />
      <AlertDialog open={Boolean(delTarget)} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.branches.delTitle", { name: delTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t("dash.branches.delDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">{t("dash.common.delete")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.common.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
